import _extends from "@babel/runtime/helpers/extends";
import { Dialog, DialogTitle, FormControl, FormGroup, FormHelperText, InputLabel, List, TextField, Typography } from '@material-ui/core';
import { deepMerge, PathHelper } from '@sensenet/client-utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defaultLocalization } from "../localization.js";
import { DefaultItemTemplate } from "./default-item-template.js";
import { ReferencePicker } from "./reference-picker.js";
const styles = {
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start'
  },
  listContainer: {
    display: 'inline-block',
    minWidth: 250,
    padding: 0,
    marginTop: 15
  }
};
export const ReferenceGrid = props => {
  var _props$localization, _props$dialogTitleCom, _props$content, _props$content2, _props$content3, _props$settings$Selec2;
  const localization = deepMerge(defaultLocalization.referenceGrid, (_props$localization = props.localization) === null || _props$localization === void 0 ? void 0 : _props$localization.referenceGrid);
  const DialogTitleComponent = (_props$dialogTitleCom = props.dialogTitleComponent) !== null && _props$dialogTitleCom !== void 0 ? _props$dialogTitleCom : DialogTitle;
  const emptyContent = useMemo(() => ({
    DisplayName: localization.addReference,
    Icon: '',
    Id: -1,
    Path: '',
    Type: '',
    Name: 'AddReference'
  }), [localization.addReference]);
  const changeContent = useMemo(() => ({
    DisplayName: localization.changeReference,
    Icon: '',
    Id: -2,
    Path: '',
    Type: '',
    Name: 'ChangeReference'
  }), [localization.changeReference]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [fieldValue, setFieldValue] = useState([]);
  const getSelected = useCallback(async () => {
    try {
      if (!props.repository) {
        throw new Error('You must pass a repository to this control');
      }
      const loadPath = props.content ? PathHelper.joinPaths('/', PathHelper.getContentUrl(props.content.Path), props.settings.Name) : '';
      const references = await props.repository.load({
        idOrPath: loadPath,
        oDataOptions: {
          select: 'all'
        }
      });
      let result = [references.d];
      if (Object.prototype.hasOwnProperty.call(references.d, 'results')) {
        result = references.d.results;
      }
      setFieldValue(result);
    } catch (error) {
      console.error(error.message);
    }
  }, [props.content, props.repository, props.settings.Name]);

  /**
   * Removes the chosen item from the grid and the field value
   */
  const removeItem = id => {
    var _props$fieldOnChange;
    const value = fieldValue.length > 1 ? fieldValue.filter(item => item.Id !== id) : [];
    (_props$fieldOnChange = props.fieldOnChange) === null || _props$fieldOnChange === void 0 ? void 0 : _props$fieldOnChange.call(props, props.settings.Name, value.map(item => item.Id));
    setFieldValue(value);
  };

  /**
   * Opens a picker to choose an item to add into the grid and the field value
   */
  const addItem = () => {
    setIsPickerOpen(true);
  };
  const handleDialogClose = () => {
    setIsPickerOpen(false);
  };
  const handleCancelClick = () => {
    handleDialogClose();
  };
  const handleOkClick = newSelection => {
    var _props$fieldOnChange2;
    (_props$fieldOnChange2 = props.fieldOnChange) === null || _props$fieldOnChange2 === void 0 ? void 0 : _props$fieldOnChange2.call(props, props.settings.Name, newSelection.map(item => item.Id));
    setFieldValue(newSelection);
    handleDialogClose();
  };
  const getDefaultValue = useCallback(async () => {
    if (!props.settings.DefaultValue || !props.repository) {
      return;
    }
    const defaultValue = props.settings.DefaultValue.split(/,|;/).filter(value => {
      var _props$settings$Selec;
      return (_props$settings$Selec = props.settings.SelectionRoots) !== null && _props$settings$Selec !== void 0 && _props$settings$Selec.length ? props.settings.SelectionRoots.some(root => PathHelper.isInSubTree(value, root)) : true;
    }).slice(0, props.settings.AllowMultiple ? undefined : 1);
    try {
      const responses = await Promise.all(defaultValue.map(async contentPath => await props.repository.load({
        idOrPath: contentPath,
        oDataOptions: {
          select: 'all'
        }
      })));
      const defaultContent = responses.map(response => response.d);
      setFieldValue(defaultContent);
    } catch (error) {
      console.error('At least one request for default reference value has failed.');
    }
  }, [props.repository, props.settings.DefaultValue, props.settings.SelectionRoots, props.settings.AllowMultiple]);
  useEffect(() => {
    if (props.actionName === 'new') {
      getDefaultValue();
    }
  }, [props.actionName, getDefaultValue]);
  useEffect(() => {
    if (props.actionName !== 'new') {
      getSelected();
    }
  }, [props.actionName, getSelected]);
  const currentParent = ((_props$content = props.content) === null || _props$content === void 0 ? void 0 : _props$content.Path.substring(0, (_props$content2 = props.content) === null || _props$content2 === void 0 ? void 0 : _props$content2.Path.lastIndexOf('/'))) || '/Root';
  switch (props.actionName) {
    case 'new':
    case 'edit':
      return /*#__PURE__*/React.createElement(FormControl, {
        "data-test": "edit-refence",
        style: styles.root,
        component: 'fieldset',
        required: props.settings.Compulsory
      }, /*#__PURE__*/React.createElement(TextField, {
        name: (_props$content3 = props.content) === null || _props$content3 === void 0 ? void 0 : _props$content3.Name,
        autoComplete: "off",
        value: fieldValue.length === 0 ? '' : 'selected',
        required: props.settings.Compulsory,
        style: {
          opacity: '0',
          pointerEvents: 'none',
          height: '0px'
        }
      }), /*#__PURE__*/React.createElement(InputLabel, {
        shrink: true,
        htmlFor: props.settings.Name
      }, props.settings.DisplayName), /*#__PURE__*/React.createElement(List, {
        dense: true,
        style: (fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.length) > 0 ? styles.listContainer : {
          ...styles.listContainer,
          width: 200
        }
      }, fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.map(item => /*#__PURE__*/React.createElement(DefaultItemTemplate, {
        content: item,
        remove: removeItem,
        add: addItem,
        key: item.Id,
        actionName: props.actionName,
        readOnly: props.settings.ReadOnly,
        repository: props.repository,
        multiple: props.settings.AllowMultiple ? props.settings.AllowMultiple : false,
        renderIcon: props.renderIcon,
        paths: props.paths
      })), !props.settings.ReadOnly ? /*#__PURE__*/React.createElement(DefaultItemTemplate, {
        content: (fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.length) > 0 && !props.settings.AllowMultiple ? changeContent : emptyContent,
        add: addItem,
        actionName: props.actionName,
        repository: props.repository,
        multiple: props.settings.AllowMultiple ? props.settings.AllowMultiple : false,
        renderIcon: props.renderIcon,
        paths: props.paths
      }) : null), !props.hideDescription && /*#__PURE__*/React.createElement(FormHelperText, null, props.settings.Description), /*#__PURE__*/React.createElement(Dialog, _extends({
        fullScreen: true,
        fullWidth: true,
        PaperProps: {
          style: {
            maxWidth: '950px'
          }
        },
        maxWidth: false,
        onClose: handleDialogClose,
        open: isPickerOpen
      }, props.dialogProps), /*#__PURE__*/React.createElement(DialogTitleComponent, {
        style: {
          width: '100%'
        }
      }, localization.referencePickerTitle), /*#__PURE__*/React.createElement(ReferencePicker, {
        defaultValue: fieldValue,
        path: ((_props$settings$Selec2 = props.settings.SelectionRoots) === null || _props$settings$Selec2 === void 0 ? void 0 : _props$settings$Selec2[0]) || '/Root',
        contextPath: currentParent,
        repository: props.repository,
        renderIcon: props.renderPickerIcon,
        handleSubmit: handleOkClick,
        handleCancel: handleCancelClick,
        fieldSettings: props.settings,
        localization: {
          cancelButton: localization.cancelButton,
          submitButton: localization.okButton
        },
        classes: props.pickerClasses
      })));
    case 'browse':
    default:
      {
        return /*#__PURE__*/React.createElement(FormControl, {
          style: styles.root
        }, /*#__PURE__*/React.createElement(Typography, {
          variant: "caption",
          gutterBottom: true
        }, props.settings.DisplayName), /*#__PURE__*/React.createElement(FormGroup, null, fieldValue ? /*#__PURE__*/React.createElement(List, {
          dense: true,
          style: styles.listContainer
        }, fieldValue.length ? fieldValue.map(item => /*#__PURE__*/React.createElement(DefaultItemTemplate, {
          content: item,
          remove: removeItem,
          add: addItem,
          key: item.Id,
          actionName: "browse",
          repository: props.repository,
          multiple: props.settings.AllowMultiple ? props.settings.AllowMultiple : false,
          renderIcon: props.renderIcon,
          paths: props.paths
        })) : /*#__PURE__*/React.createElement(Typography, {
          variant: "body1",
          gutterBottom: true
        }, localization.noValue)) : /*#__PURE__*/React.createElement(Typography, {
          variant: "body1",
          gutterBottom: true
        }, localization.noValue)));
      }
  }
};
//# sourceMappingURL=reference-grid.js.map